import React, { useEffect, useState } from 'react';
import { Section } from './Section';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Globe, School, ChevronLeft, ChevronRight } from 'lucide-react';
interface EventCardProps {
  title: string;
  description: string;
  index: number;
  logo?: string;
}
function EventCard({
  title,
  description,
  index,
  logo
}: EventCardProps) {
  return <motion.div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 h-full flex flex-col" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: index * 0.1
  }} data-id="element-69">
      <div className="flex items-center gap-3 mb-3" data-id="element-70">
        {logo && <div className="w-10 h-10 flex-shrink-0" data-id="element-71">
            <img src={logo} alt={title} className="w-full h-full object-contain" data-id="element-72" />
          </div>}
        <h3 className="text-xl font-semibold" data-id="element-73">{title}</h3>
      </div>
      <p className="text-gray-600 flex-grow" data-id="element-74">{description}</p>
    </motion.div>;
}
interface InitiativeCardProps {
  title: string;
  description: string;
  website?: string;
  icon: React.ReactNode;
  index: number;
}
function InitiativeCard({
  title,
  description,
  website,
  icon,
  index
}: InitiativeCardProps) {
  return <motion.div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 h-full flex flex-col" initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.5,
    delay: index * 0.1
  }} data-id="element-75">
      <div className="flex items-center gap-3 mb-3" data-id="element-76">
        <div className="p-2 bg-gray-100 rounded-lg" data-id="element-77">{icon}</div>
        <h3 className="text-xl font-semibold" data-id="element-78">{title}</h3>
      </div>
      <p className="text-gray-600 mb-2 flex-grow" data-id="element-79">{description}</p>
      {website && <a href={`https://${website}`} target="_blank" rel="noopener noreferrer" className="text-gray-900 font-medium hover:underline inline-flex items-center gap-1" data-id="element-80">
          <Globe size={14} data-id="element-81" />
          {website}
        </a>}
    </motion.div>;
}
export function Impact() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const events = [{
    title: 'TEDx Warri',
    description: 'Spoke to a hall of 700+ young people on how Great learning experiences shape future leaders, inspiring the next generation to take charge of their educational journey.',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0a/TED_Logo_2.svg/512px-TED_Logo_2.svg.png'
  }, {
    title: 'TEDx University of Benin',
    description: 'Shared experiences on how university students can take advantage of their time in university to design and curate learning experiences that empower them for the future they desire.',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0a/TED_Logo_2.svg/512px-TED_Logo_2.svg.png'
  }, {
    title: 'YALI Network Benin City',
    description: 'Facilitated a Public Speaking and Networking Session with practical illustrations on how young people can position themselves for growth and impact in their communities and careers.',
    logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tv631tBafuSNuEBBA9hM6B/yali_logo.png'
  }, {
    title: 'PanAfrican Capital Foundation',
    description: 'Facilitated a networking session and taught 20+ women on best approaches to networking, sharing my book "The 10 Commandments of Networking" to help them get it right.',
    logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mtgTv26EoWm2UjtNfmT7sV/Panafrican.jpg'
  }, {
    title: 'NIMSA',
    description: 'Keynote speaker on Personal Development for young change makers, sharing key experiences and practical strategies for sustainable impact and growth.',
    logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6pyTyGNFUhpYY4bbSKmiRJ/Nimsa.jpg'
  }, {
    title: 'SkillzHQ',
    description: 'Senior Youth Engagement and Learning Officer at SkillzHQ Limited. Also serving as a course instructor on the newly launched Executive Assistant Mastery Course - Handling the session "Artificial Intelligence for Executive Assistants".',
    logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pT23cACXux2UnYjrDbuBny/skillzhq.png'
  }, {
    title: 'GOTNI Emerging Leaders Conference 2023',
    description: 'Participated as a panelist at the GOTNI Emerging Leaders Conference 2023, speaking on the future of work and the skills needed to position oneself for success in the evolving job market.',
    logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rEXgEcHFwJJSxzL56pCbMY/Gotni.jpg'
  }, {
    title: 'GIZ/GOPA-IOM Program',
    description: 'Worked as Technical Support for a GIZ/GOPA-IOM Program, a collaborative effort to reintegrate returnees and equip them with entrepreneurial skills using the "Create" component of the ICSS Entrepreneurship Model in Ekpoma.',
    logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/28nwky3gc5wUaK6qEFdhdK/GIZ.png'
  }, {
    title: 'Junior Chambers International',
    description: 'Facilitated over 5 upskilling sessions on Networking, AI tools, Interview skills, and Communication skills for JCI UNIBEN and JCI Benin Metro Nigeria, impacting over 1000 participants so far.',
    logo: 'https://www.jci.cc/images/JCI-Logo.png'
  }];
  // Group events into slides of 3
  const slidesCount = Math.ceil(events.length / 3);
  const slides = Array.from({
    length: slidesCount
  }, (_, i) => events.slice(i * 3, (i + 1) * 3));
  // Auto-advance the carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slidesCount);
    }, 7000);
    return () => clearInterval(interval);
  }, [slidesCount]);
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % slidesCount);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + slidesCount) % slidesCount);
  };
  const initiatives = [{
    title: 'GoABitFurther Africa',
    description: 'A social learning community I founded as an undergraduate to expose university students to the demands of the ever-evolving workspace, providing affordable mentorship to get them future-proof! Reached 7000+ young people over 7 years.',
    website: 'goabitfurtherafrica.site',
    icon: <Globe size={24} data-id="element-82" />
  }, {
    title: 'The Networking Salon',
    description: 'Curating a community that empowers and creates opportunities for culture shapers to find the network that gets them ahead in their careers. With activated communities in Berlin, Houston Texas, Benin City, Lagos, Abuja, Warri and more.',
    website: 'thenetworkingsalon.live',
    icon: <Users size={24} data-id="element-83" />
  }, {
    title: 'School Tour Project',
    description: 'Currently engaging with school owners in Benin City, Edo State, to train their teachers and Senior Schoolers on effective use of Artificial Intelligence to increase retention and classroom engagement.',
    icon: <School size={24} data-id="element-84" />
  }];
  return <Section id="impact" title="Impact & Speaking Engagements" className="bg-gray-50" data-id="element-85">
      <div className="max-w-6xl mx-auto" data-id="element-86">
        <motion.div className="flex items-center justify-center gap-4 mb-12 p-6 bg-gray-900 text-white rounded-2xl shadow-lg" initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-87">
          <Users size={40} data-id="element-88" />
          <h3 className="text-2xl md:text-3xl font-bold" data-id="element-89">
            Impacted 25,000+ individuals across Africa and beyond
          </h3>
        </motion.div>
        <h3 className="text-2xl font-semibold mb-6" data-id="element-90">Speaking Engagements</h3>
        <div className="relative mb-16" data-id="element-91">
          <div className="overflow-hidden" data-id="element-92">
            <AnimatePresence mode="wait" data-id="element-93">
              <motion.div key={currentSlide} initial={{
              opacity: 0,
              x: 100
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -100
            }} transition={{
              duration: 0.5
            }} className="grid md:grid-cols-3 gap-6" data-id="element-94">
                {slides[currentSlide].map((event, index) => <EventCard key={event.title} title={event.title} description={event.description} index={index} logo={event.logo} data-id="element-95" />)}
              </motion.div>
            </AnimatePresence>
          </div>
          {/* Navigation arrows */}
          <button onClick={prevSlide} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white p-2 rounded-full shadow-md z-10" aria-label="Previous slide" data-id="element-96">
            <ChevronLeft size={20} data-id="element-97" />
          </button>
          <button onClick={nextSlide} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white p-2 rounded-full shadow-md z-10" aria-label="Next slide" data-id="element-98">
            <ChevronRight size={20} data-id="element-99" />
          </button>
          {/* Dots navigation */}
          <div className="flex justify-center mt-6 gap-2" data-id="element-100">
            {slides.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`w-2.5 h-2.5 rounded-full transition-colors ${index === currentSlide ? 'bg-gray-800' : 'bg-gray-300'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-101" />)}
          </div>
        </div>
        <h3 className="text-2xl font-semibold mb-6" data-id="element-102">Initiatives & Projects</h3>
        <div className="grid md:grid-cols-3 gap-6" data-id="element-103">
          {initiatives.map((initiative, index) => <InitiativeCard key={initiative.title} title={initiative.title} description={initiative.description} website={initiative.website} icon={initiative.icon} index={index} data-id="element-104" />)}
        </div>
      </div>
    </Section>;
}